/**
 * Le 409 `GED_FILE_LEGAL_RETENTION`, lu par son CODE — le FILET de la garde AO.
 *
 * ## 🔴 B4, mesuré le 2026-09-19 : la masse était traduite, l'unitaire non
 *
 * `grep GED_FILE_LEGAL_RETENTION frontend/src` rendait **0 occurrence** hors des
 * deux clés de MASSE (`ged.bulk.reason.ged_file_legal_retention_one` / `_other`,
 * servies par `toBulkImpact`). Sur la porte UNITAIRE — le coffre
 * (`api/ged_files.py` l.261, geste « supprimer ») comme la corbeille des quatre
 * onglets de fiche (`soft_delete_ou_refus`) — `phraseDuRefusDAppelDOffres`
 * rendait `null` sur ce code, et le toast retombait sur le message du SERVEUR :
 * « Ce document doit être conservé : on ne peut pas le supprimer. » Une phrase
 * juste, soignée… et **française dans l'interface anglaise**. Cocher dix pièces
 * donnait donc une phrase traduite, en supprimer une seule non.
 *
 * ## Pourquoi ce refus vit à côté de celui de l'appel d'offres, et pas dedans
 *
 * Il est levé par la MÊME fonction (`refuser_une_piece_dappel_doffres`,
 * `_deletion_guards_portee.py` l.157) mais il dit le CONTRAIRE : la garde de la
 * consultation vient justement de se retirer (fiche hors d'atteinte, module
 * `TENDERS` retiré du plan), et c'est l'obligation de conservation qui tient
 * encore. D'où un module à part, et une phrase qui ne nomme **aucune
 * consultation** : le serveur n'en envoie pas, et pour cause — l'y envoyer
 * serait le cul-de-sac que la décision T1 vient de fermer.
 *
 * ⚠️ Une phrase SANS issue, et c'est délibéré. Toutes les autres phrases de ce
 * dossier finissent par « Passez par la fiche de l'appel d'offres » ; celle-ci
 * ne peut promettre aucun geste, parce qu'il n'y en a pas. Lui en inventer un
 * (« videz la corbeille », « demandez à un administrateur ») enverrait
 * l'utilisateur essayer quelque chose que le serveur refusera pareil.
 *
 * ⚠️ La clé est écrite EN ENTIER, DANS l'appel à `t` : une clé construite
 * échapperait à tout extracteur, et une clé absente des deux catalogues laisse
 * la parité verte (mesure du 2026-09-17, reprise dans le hub).
 */

import type { TFunction } from 'i18next'

/** Le filet de C7 : la pièce est sous conservation légale, elle ne part pas. */
export const GED_FILE_LEGAL_RETENTION = 'GED_FILE_LEGAL_RETENTION'

/** `t` rend `undefined` tant qu'i18next n'est pas initialisé (mesuré sur la
 *  23.16.8) : une chaîne vide ou absente ne doit jamais devenir le toast. */
function texte(valeur: unknown): string | null {
    return typeof valeur === 'string' && valeur.trim() ? valeur : null
}

/**
 * La phrase du refus de conservation légale, dans la langue de l'écran.
 *
 * `null` si rien n'est traduit — l'appelant retombe alors sur le message du
 * serveur, qui reste juste. Aucun paramètre : ce refus n'a ni référence, ni
 * nombre, ni geste à interpoler. Il n'arrive d'ailleurs que sur « supprimer »
 * (`refuser_une_piece_dappel_doffres` : `if geste == "supprimer" and
 * conservation_legale`), donc la phrase peut dire « supprimer » sans détour.
 */
export function phraseDeConservationLegale(t: TFunction): string | null {
    return texte(
        t('ged.refus.legal_retention', {
            defaultValue:
                'Ce document est sous conservation légale : il doit être conservé et ne peut pas être supprimé.',
        }),
    )
}
